import i18n from "src/i18n/i18n";
import { getStore } from "src/store";
import { localizationDefaults } from "src/utils/localization";
import { AccountDisclaimers } from "Types/api/AccountDisclaimers";
import { AccountState } from "Types/state";

export const hasLockedPricing = (account: AccountState) => {
    return !!account && !!account.integration_settings && !!account.integration_settings.locked_pricing;
};

/**
 * Checks if the given region is considered the USA.
 * undefined and empty string regions are considered US as part of our efforts to default back to the most common use case
 * Ideally the array comes from a localization config someday, but for now we only support 'canada' as a non US
 * @param region
 */
export const isUS = (region?: string): boolean => {
    return !["CA"].includes(`${region}`);
};

export function hasAccessoriesEnabled(account: AccountState) {
    return !!(account && account.accessories_enabled);
}

// Warning: in order for strikethrough to show up, MSRP needs to be !== price
export const showStrikethroughForMSRP = (account: AccountState): boolean =>
    !!(
        account &&
        account.integration_settings &&
        account.integration_settings.Financing &&
        account.integration_settings.Financing.ui &&
        account.integration_settings.Financing.ui.strikethrough_msrp
    );

export const canShowMSRP = (account: AccountState): boolean =>
    !!(
        account &&
        account.integration_settings &&
        account.integration_settings.Financing &&
        account.integration_settings.Financing.ui &&
        account.integration_settings.Financing.ui.show_msrp
    );

/**
 * Gets the country code from the store's account object, or optionally from a given account.
 * If any case returns no country code, we return the default country for our localization.
 */
export const getCountryOrDefault = (account: AccountState | undefined): string => {
    return (account && account.address && account.address.country) || localizationDefaults.country;
};

/**
 * Gets the currency code from the store's account object, or optionally from a given account.
 * If any case returns no currency code, we return the default currency for our localization.
 */
export const getCurrencyOrDefault = (account: AccountState | undefined): string => {
    return (account && account.localization && account.localization.currency) || localizationDefaults.currency;
};

/**
 * Wrapper for getting the country code that takes in an optional account to work off of,
 * Otherwise gets a new copy of .getState from the root redux store
 */
export const getCountryFromAccount = (account?: AccountState): string => {
    if (!account) {
        account = getAccountFromStore();
    }
    return getCountryOrDefault(account);
};

/**
 * Wrapper for getting the currency code that takes in an optional account to work off of,
 * Otherwise gets a new copy of .getState from the root redux store
 */
export const getCurrencyFromAccount = (account?: AccountState): string => {
    if (!account) {
        account = getAccountFromStore();
    }
    return getCurrencyOrDefault(account);
};

/**
 * Get the Account object from the Store
 */
export const getAccountFromStore = (): AccountState | undefined => {
    const store = getStore();
    const appState = store && store.getState();
    return appState && appState.account;
};

/**
 * Get the account disclaimer based on the key supplied. If it doesn't exist, returns the value for the translation key.
 * @param account: AccountState
 * @return {(disclaimerKey: keyof AccountDisclaimers) => (translationKey: string): string}
 */
export const getAccountDisclaimer = (account: AccountState) => (disclaimerKey: keyof AccountDisclaimers) => (
    translationKey: string
): string => {
    let message;
    if (account && account.account_disclaimers) {
        message = account.account_disclaimers[disclaimerKey];
    }
    if (!message) {
        message = i18n.t(translationKey); // fall back to the value for the translation key
    }
    return message;
};
